import { SimpleError } from '@common/errors';
import { cloneObject } from '../classes/Cloneable.js';
import { encodeObject } from '../classes/Encodeable.js';
import { getId, hasId } from '../classes/Identifiable.js';
import { isPatchable } from '../classes/Patchable.js';
function isMove(val) {
    return val.move !== undefined;
}
function isPut(val) {
    return val.put !== undefined;
}
function isDelete(val) {
    return val.delete !== undefined;
}
function isPatch(val) {
    return val.patch !== undefined;
}
/**
 * Records ordered insert, move, delete, and patch operations for an array.
 * String/number elements identify themselves; objects are identified by their id.
 * Use unique identifiers so each operation targets an unambiguous item.
 */
export class PatchableArray {
    _isPatchableArray = true;
    changes;
    constructor(changes) {
        this.changes = changes ?? [];
    }
    clone() {
        // Deep clone self
        const cloned = new PatchableArray();
        cloned.merge(this);
        return cloned;
    }
    merge(other) {
        // We need to clone the incoming changes, because otherwise we would keep the same reference
        // to individual changes we might still change
        for (const change of other.changes) {
            this.changes.push(cloneObject(change));
        }
    }
    /** Composes changes into a clone; a plain array replaces the accumulated patch. */
    patch(patch) {
        if (Array.isArray(patch)) {
            // Transform itself into a normal array. Override all existing patches
            return patch.slice();
        }
        // Deep clone self
        const cloned = this.clone();
        for (const change of patch.changes) {
            // Apply this change
            if (isMove(change)) {
                cloned.addMove(change.move, change.afterId);
            }
            else if (isPut(change)) {
                cloned.addPut(change.put, change.afterId);
            }
            else if (isDelete(change)) {
                cloned.addDelete(change.delete);
            }
            else if (isPatch(change)) {
                cloned.addPatch(change.patch);
            }
            else {
                throw new Error('Invalid change: ' + JSON.stringify(change));
            }
        }
        return cloned;
    }
    /**
     * Records an insertion. An object with an existing id replaces that object
     * when applied (since v2.23.0); scalar values can be inserted more than once.
     * @param value Value to insert in the array
     * @param after
     *      - undefined (or not provided) = insert at the end
     *      - null = insert at the start
     *      - id = insert after the provided id. If the provided id is not found, it is appended at the end
     */
    addPut(value, after) {
        this.changes.push({ afterId: after, put: value });
    }
    /** Moves an item after an id, or to the start for null; an absent target id means the end. */
    addMove(item, after) {
        this.changes.push({ afterId: after, move: item });
    }
    hasChanges(item) {
        for (const change of this.changes) {
            if (isMove(change)) {
                // ok
            }
            else if (isPut(change)) {
                if (getId(change.put) === item) {
                    return true;
                }
            }
            else if (isDelete(change)) {
                if (change.delete === item) {
                    return true;
                }
            }
            else if (isPatch(change)) {
                if (getId(change.patch) === item) {
                    return true;
                }
            }
            else {
                throw new Error('Invalid change: ' + JSON.stringify(change));
            }
        }
        return false;
    }
    filter(item) {
        const construct = this.constructor;
        const n = new construct();
        const newCurrentChanges = [];
        for (const change of this.changes) {
            if (isMove(change)) {
                newCurrentChanges.push(change);
            }
            else if (isPut(change)) {
                if (getId(change.put) === item) {
                    n.changes.push(change);
                }
                else {
                    newCurrentChanges.push(change);
                }
            }
            else if (isDelete(change)) {
                if (change.delete === item) {
                    n.changes.push(change);
                }
                else {
                    newCurrentChanges.push(change);
                }
            }
            else if (isPatch(change)) {
                if (getId(change.patch) === item) {
                    n.changes.push(change);
                }
                else {
                    newCurrentChanges.push(change);
                }
            }
            else {
                throw new Error('Invalid change: ' + JSON.stringify(change));
            }
        }
        this.changes = newCurrentChanges;
        return n;
    }
    /**
     * Records changes to an item identified by the patch's id.
     * Composes with an existing put/patch for that id. Applying to an absent item
     * does nothing and never creates a default object.
     */
    addPatch(value) {
        const id = getId(value);
        const otherPut = this.changes.findIndex(e => isPut(e) && getId(e.put) == id);
        if (otherPut !== -1) {
            const other = this.changes[otherPut];
            if (isPatchable(other.put)) {
                this.changes.splice(otherPut, 1, {
                    put: other.put.patch(value),
                    afterId: other.afterId,
                });
            }
            else {
                this.changes.splice(otherPut, 1, {
                    put: value,
                    afterId: other.afterId,
                });
            }
            return;
        }
        const otherPatch = this.changes.findIndex(e => isPatch(e) && getId(e.patch) == id);
        if (otherPatch !== -1) {
            const other = this.changes[otherPatch];
            if (isPatchable(other.patch)) {
                this.changes.splice(otherPatch, 1, {
                    patch: other.patch.patch(value),
                });
            }
            else {
                this.changes.splice(otherPatch, 1, {
                    patch: value,
                });
            }
            return;
        }
        this.changes.push({ patch: value });
    }
    /** Records removal by id; cancels a pending put for that id instead of adding a delete. */
    addDelete(id) {
        // Remove all puts, patches and moves
        const otherPut = this.changes.findIndex(e => isPut(e) && getId(e.put) == id);
        if (otherPut !== -1) {
            // if it had a put, remove the put but don't add a delete
            this.changes.splice(otherPut, 1);
            return;
        }
        else {
            const otherPatch = this.changes.findIndex(e => isPatch(e) && getId(e.patch) == id);
            if (otherPatch !== -1) {
                this.changes.splice(otherPatch, 1);
            }
        }
        this.changes.push({ delete: id });
    }
    /**
     * Applies operations to a copy of the array, leaving the input unchanged.
     * Missing patch IDs are ignored; missing move/delete IDs emit a warning.
     */
    applyTo(array) {
        const newArray = array.slice(0);
        for (const change of this.changes) {
            // Apply this change
            if (isMove(change)) {
                // First do a delete of this value
                const index = newArray.findIndex(e => getId(e) === change.move);
                if (index !== -1) {
                    const value = newArray[index];
                    newArray.splice(index, 1);
                    // Insert it again
                    // null = inserting at the beginning
                    // not found = inserting at the end
                    let afterIndex = -1;
                    if (change.afterId !== null) {
                        afterIndex = newArray.findIndex(e => getId(e) === change.afterId);
                        if (afterIndex === -1) {
                            afterIndex = newArray.length - 1;
                        }
                    }
                    newArray.splice(afterIndex + 1, 0, value);
                }
                else {
                    // maybe throw here?
                    console.warn('Could not find element with id ' + change.move);
                }
            }
            else if (isPut(change)) {
                // null = inserting at the beginning
                // not found = inserting at the end
                let afterIndex = -1;
                if (change.afterId !== null) {
                    afterIndex = newArray.findIndex(e => getId(e) === change.afterId);
                    if (afterIndex === -1) {
                        afterIndex = newArray.length - 1;
                    }
                }
                if (change.afterId === undefined) {
                    afterIndex = newArray.length - 1;
                }
                if (hasId(change.put)) {
                    // Delete any items with the same id first
                    const existingIndex = newArray.findIndex(e => getId(e) === getId(change.put));
                    if (existingIndex !== -1) {
                        newArray.splice(existingIndex, 1);
                        // Adjust afterIndex if needed
                        if (existingIndex < afterIndex) {
                            afterIndex--;
                        }
                    }
                }
                newArray.splice(afterIndex + 1, 0, change.put);
            }
            else if (isDelete(change)) {
                // First do a delete of this value
                const index = newArray.findIndex(e => getId(e) === change.delete);
                if (index !== -1) {
                    newArray.splice(index, 1);
                }
                else {
                    console.warn('Could not find element with id ' + change.delete);
                }
            }
            else if (isPatch(change)) {
                // First do a delete of this value
                const index = newArray.findIndex(e => getId(e) === getId(change.patch));
                if (index !== -1) {
                    // Patch!
                    const value = newArray[index];
                    if (isPatchable(value)) {
                        newArray.splice(index, 1, value.patch(change.patch));
                    }
                    else {
                        newArray.splice(index, 1, change.patch);
                    }
                }
            }
            else {
                throw new Error('Invalid change: ' + JSON.stringify(change));
            }
        }
        return newArray;
    }
    encode(context) {
        return {
            _isPatch: true,
            changes: this.changes.map((change) => {
                if (isMove(change)) {
                    // First do a delete of this value
                    return {
                        afterId: change.afterId,
                        move: encodeObject(change.move, context),
                    };
                }
                else if (isPut(change)) {
                    // First do a delete of this value
                    return {
                        afterId: change.afterId,
                        put: encodeObject(change.put, context),
                    };
                }
                else if (isDelete(change)) {
                    return {
                        delete: change.delete,
                    };
                }
                else if (isPatch(change)) {
                    // First do a delete of this value
                    return {
                        patch: encodeObject(change.patch, context),
                    };
                }
            }),
        };
    }
    getPuts() {
        return this.changes.filter(change => isPut(change));
    }
    getPatches() {
        return this.changes.filter(change => isPatch(change)).map((p) => p.patch);
    }
    getDeletes() {
        return this.changes.filter(change => isDelete(change)).map((p) => p.delete);
    }
    getMoves() {
        return this.changes.filter(change => isMove(change));
    }
}
export class PatchableArrayItemDecoder {
    putDecoder;
    patchDecoder;
    idDecoder;
    constructor(putDecoder, patchDecoder, idDecoder) {
        this.putDecoder = putDecoder;
        this.patchDecoder = patchDecoder;
        this.idDecoder = idDecoder;
    }
    decode(data) {
        const put = data.optionalField('put');
        if (put !== undefined) {
            // throw decoding errors from putDecoder and idDecoder
            return {
                put: put.decode(this.putDecoder),
                afterId: data.undefinedField('afterId')?.nullable(this.idDecoder),
            };
        }
        const move = data.optionalField('move');
        if (move !== undefined) {
            return {
                move: move.decode(this.idDecoder),
                afterId: data.field('afterId').nullable(this.idDecoder),
            };
        }
        const d = data.optionalField('delete');
        if (d !== undefined) {
            return {
                delete: d.decode(this.idDecoder),
            };
        }
        const patch = data.optionalField('patch');
        if (patch !== undefined) {
            return {
                patch: patch.decode(this.patchDecoder),
            };
        }
        throw new SimpleError({
            code: 'invalid_field',
            message: 'Expected put, move, patch or delete',
            field: data.currentField,
        });
    }
}
export class PatchableArrayDecoder {
    putDecoder;
    patchDecoder;
    idDecoder;
    constructor(putDecoder, patchDecoder, idDecoder) {
        this.putDecoder = putDecoder;
        this.patchDecoder = patchDecoder;
        this.idDecoder = idDecoder;
    }
    decode(data) {
        if (Array.isArray(data.value)) {
            console.warn('Found legacy patchable array. Make sure to use the new patchable array encoding, as this will get removed and replaced with a PUT in future versions.');
            // Legacy encode version
            // This will remain supported for a period
            // but is deprecated
            return new PatchableArray(data.array(new PatchableArrayItemDecoder(this.putDecoder, this.patchDecoder, this.idDecoder)));
        }
        const changes = data.field('changes').array(new PatchableArrayItemDecoder(this.putDecoder, this.patchDecoder, this.idDecoder));
        return new PatchableArray(changes);
    }
    isDefaultValue(value) {
        return value instanceof PatchableArray && value.changes.length === 0;
    }
    getDefaultValue() {
        return new PatchableArray();
    }
}
//# sourceMappingURL=PatchableArray.js.map